import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import { trackEvent } from '../lib/analytics';

const Header = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isRTL = i18n.language === 'ar';

  const navItems = [
    { label: t('nav.about'), href: '#about' },
    { label: t('nav.services'), href: '#services' },
    { label: t('nav.process'), href: '#process' },
    { label: t('nav.whyMe'), href: '#why-me' },
    { label: t('nav.faq'), href: '#faq' },
    { label: t('nav.contact'), href: '#contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock background scroll while the mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const langPrefix = i18n.language && i18n.language !== 'en' ? `/${i18n.language}` : '';
  const homeHref = langPrefix || '/';

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Section not on this page (e.g. Privacy/Terms) → go to the homepage anchor
      window.location.href = `${homeHref}${href}`;
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    if (document.querySelector('#services')) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.location.href = homeHref;
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-black/95 backdrop-blur-sm border-b border-white/10'
          : 'bg-transparent'
      }`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-[7.6923%]">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a 
            href={homeHref}
            onClick={handleLogoClick}
            className="text-xl font-semibold text-white hover:text-[#00FFD1] transition-colors duration-300"
          >
            <span className="text-[#00FFD1]">&lt;</span>
            OBN
            <span className="text-[#00FFD1]">/&gt;</span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="text-[#4D4D4D] text-sm font-normal hover:text-white transition-colors duration-300 relative group"
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-[#00FFD1] transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </nav>

          {/* Right Side - Language Selector & CTA */}
          <div className="flex items-center gap-4">
            <LanguageSelector />
            
            {/* CTA Button */}
            <a
              href="#contact"
              onClick={(e) => { trackEvent('book_call_click', { location: 'header' }); handleNavClick(e, '#contact'); }}
              className="hidden md:flex btn-primary text-sm px-5 py-3 min-h-0"
            >
              {t('nav.getInTouch')}
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-white hover:text-[#00FFD1] transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed inset-0 top-20 bg-black/98 backdrop-blur-lg transition-[transform,opacity,visibility] duration-500 ${
          isMobileMenuOpen
            ? 'translate-x-0 opacity-100 visible'
            : `${isRTL ? '-translate-x-full' : 'translate-x-full'} opacity-0 invisible pointer-events-none`
        }`}
      >
        <nav className="flex flex-col items-center justify-center h-full gap-8 pb-20">
          {navItems.map((item, index) => (
            <a
              key={item.label}
              href={item.href}
              onClick={(e) => handleNavClick(e, item.href)}
              className="text-2xl text-white hover:text-[#00FFD1] transition-colors duration-300"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {item.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={(e) => handleNavClick(e, '#contact')}
            className="btn-primary mt-4"
          >
            {t('nav.getInTouch')}
          </a>
        </nav>
      </div>
    </header>
  );
};

export default Header;
